/**
 * The flat node list `GET /api/spaces/:id/nodes` returns, assembled into the
 * tree §4.2 describes.
 *
 * Lifted verbatim from `routes/spaces/detail.tsx` rather than rewritten, so the
 * space page and every other surface that draws the tree agree about the one
 * case that is easy to get wrong — see the orphan rule below. That page still
 * has its own copy at the time of writing; repointing it at this module is a
 * one-line change and is the whole reason this is a module rather than a
 * closure inside the component.
 *
 * Nothing here decides what a person may see. The list arrives already filtered
 * by `permission_visible_nodes` (phase-0 §4.9), and a shape function that also
 * dropped rows would be a second, unpinned answer to a question the database
 * has already answered.
 */

import type { TreeNode } from '../../api/types.ts';

export interface NodeTreeItem {
  node: TreeNode;
  children: NodeTreeItem[];
  /** Zero at the top of *this list*, which is not necessarily the top of the space. */
  depth: number;
}

export function buildNodeTree(nodes: readonly TreeNode[]): NodeTreeItem[] {
  const byId = new Set(nodes.map((node) => node.id));
  const childrenOf = new Map<string, TreeNode[]>();
  const roots: TreeNode[] = [];

  for (const node of nodes) {
    // A node whose parent this person cannot reach still surfaces, at the top.
    // §4.6 permits exactly that — a node-level allow punching through a
    // space-level deny — and pruning it would hide a case the model supports.
    // It also means `depth` is a rendering hint and not a claim about ancestry.
    if (node.parentNodeId && byId.has(node.parentNodeId)) {
      const siblings = childrenOf.get(node.parentNodeId) ?? [];
      siblings.push(node);
      childrenOf.set(node.parentNodeId, siblings);
    } else {
      roots.push(node);
    }
  }

  const build = (node: TreeNode, depth: number): NodeTreeItem => ({
    node,
    depth,
    children: (childrenOf.get(node.id) ?? [])
      .sort((a, b) => a.position - b.position)
      .map((child) => build(child, depth + 1)),
  });

  return roots.sort((a, b) => a.position - b.position).map((root) => build(root, 0));
}
